/**
 * Мит-сессии (DEV-1947): общий протокол созвона поверх личных заметок.
 *
 * Встреча рождается первым приглашением, а не входом в мит-режим: человек,
 * который развернул ленту и пишет один, встречи не начинает. Участник — тот,
 * кто пришёл: приглашение само по себе заметок не открывает.
 *
 * События встречи приезжают потоком NDJSON — той же дорогой, что и ответ
 * ассистента, и по той же причине. Строка потока несёт только вид события,
 * дальше клиент перечитывает ленту: склеенная из кусочков она разошлась бы с
 * серверной, а перечитанная согласована всегда.
 */
import { planApi } from '@/api'
import { ensureFreshToken } from '@/api/http'

import { readNdjson } from '../assistant/stream'

import type {
  TrackerMeetingInvite,
  TrackerMeetingSession,
  TrackerMeetingState,
} from '@/api/clients/tracker-plan'

const baseURL = import.meta.env.VITE_API_BASE_URL || ''

/** Участник встречи в том виде, в каком его рисует шапка мит-режима */
export interface MeetingParticipant {
  userId: string
  name: string
  avatarUrl: string
  /** Пришёл ли. Позванный, но не пришедший, заметок не видит */
  joined: boolean
}

export interface MeetingSession {
  id: string
  authorId: string
  /** Завершить встречу может только автор */
  iAmAuthor: boolean
  createdAt: string
  participants: MeetingParticipant[]
}

export interface MeetingInvite {
  sessionId: string
  invitedByName: string
  invitedAt: string
}

export interface MeetingState {
  session: MeetingSession | null
  invites: MeetingInvite[]
}

const toSession = (session: TrackerMeetingSession): MeetingSession => ({
  id: session.id ?? '',
  authorId: session.authorId ?? '',
  iAmAuthor: session.iAmAuthor ?? false,
  createdAt: session.createdAt ?? '',
  participants: (session.participants ?? []).map((participant) => ({
    userId: participant.userId ?? '',
    name: participant.displayName ?? '',
    avatarUrl: participant.avatarUrl ?? '',
    joined: participant.joined ?? false,
  })),
})

const toInvite = (invite: TrackerMeetingInvite): MeetingInvite => ({
  sessionId: invite.sessionId ?? '',
  invitedByName: invite.invitedByName ?? '',
  invitedAt: invite.invitedAt ?? '',
})

const toState = (state: TrackerMeetingState): MeetingState => ({
  session: state.session?.id ? toSession(state.session) : null,
  invites: (state.invites ?? []).map(toInvite),
})

export const getMeetingState = async (): Promise<MeetingState> => {
  const { data } = await planApi.planServiceGetMeetingState()

  return toState(data)
}

export const inviteToMeeting = async (userIds: string[]): Promise<MeetingState> => {
  const { data } = await planApi.planServiceInviteToMeeting({ userIds })

  return toState(data)
}

export const joinMeeting = async (sessionId: string): Promise<MeetingState> => {
  const { data } = await planApi.planServiceJoinMeeting({ sessionId })

  return toState(data)
}

export const endMeeting = async (sessionId: string): Promise<void> => {
  await planApi.planServiceEndMeeting({ sessionId })
}

/** Виды событий потока; повторяют `dto.MeetingEventKind` бэкенда */
export type MeetingEventKind = 'open' | 'ping' | 'note' | 'participants' | 'ended'

interface MeetingStreamOptions {
  signal: AbortSignal
  onEvent: (kind: MeetingEventKind) => void
}

/**
 * Поток событий одной встречи. Живёт, пока открыт мит-режим.
 *
 * Обрыв тут — обычное дело: выкат сервиса, спящий ноутбук, метро. Поэтому
 * функция просто заканчивается, а переподключение и догон пропущенного —
 * забота стора: только он знает, что именно перечитывать.
 */
export const streamMeeting = async (
  sessionId: string,
  options: MeetingStreamOptions,
): Promise<void> => {
  const response = await fetch(
    `${baseURL}/api/v1/tracker/plan/meeting/${sessionId}/stream`,
    {
      headers: { Authorization: `Bearer ${await ensureFreshToken()}` },
      signal: options.signal,
    },
  )

  if (!response.ok || !response.body) {
    throw new Error(`meeting stream: ${response.status}`)
  }

  await readNdjson<{ kind?: MeetingEventKind }>(response.body, (event) => {
    options.onEvent(event.kind ?? 'ping')
  })
}
